import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigation } from 'app/hooks/navigation';
import { localeAwareFormat } from 'app/lib/date';
import { useDeleteMyProfileUnavailability } from 'app/network/profile';
import { Trans } from 'react-i18next';
import Toast from 'react-native-toast-message';
import { IconClock, IconPencil } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

export const UnavailabilityStatus = function UnavailabilityStatus({
  startDate,
  endDate,
  onDeleted,
  planned = false,
}: {
  startDate: string;
  endDate: string;
  onDeleted?: () => void;
  planned?: boolean;
}) {
  const { t } = useScopedTranslation();
  const navigation = useNavigation();

  const deleteUnavailability = useDeleteMyProfileUnavailability({
    onSuccess: () => {
      onDeleted?.();
      Toast.show({
        type: 'success',
        position: 'bottom',
        bottomOffset: 108,
        text1: t('deleteStatusToast.title', { ns: 'profile-overview' }),
        visibilityTime: 10_000,
      });
    },
  });

  const deleteStatus = async () => {
    await deleteUnavailability.mutateAsync();
  };

  const onPress = () => {
    navigation.navigate('unavailable', { startDate, endDate }, { pop: true });
  };

  return (
    <Column
      marginTop="spacing.general.x4"
      padding="spacing.general.x4"
      backgroundColor="surface.tertiary-neutral"
      borderRadius="radius.xl"
      gap="spacing.general.x2"
      testID="profile-unavailable-status"
    >
      <Text fontWeight="bold" fontSize={22}>
        {t('unavailableStatusTitle')}
      </Text>

      <Row justifyContent="space-between" alignItems="center" gap="spacing.general.x2">
        <Column flex={1} flexShrink={1} gap={planned ? undefined : 'spacing.general.x2'}>
          {planned ? null : (
            <Row
              backgroundColor="white"
              padding="spacing.general.x1"
              borderRadius="radius.sm"
              alignItems="center"
              alignSelf="flex-start"
              gap="spacing.general.x2"
              testID="profile-unavailable-status-badge"
            >
              <IconClock strokeWidth={ICON_STROKE_WIDTH} size={24} />
              <Text fontSize={18} color="text.secondary" fontWeight="bold">
                {t('unavailableStatusBadge')}
              </Text>
            </Row>
          )}

          <Text flexShrink={1} variant="bodySmall" testID="profile-unavailable-status-dates">
            <Trans
              components={{ strong: <Text variant="bodySmallMedium" /> }}
              i18nKey={planned ? 'unavailableStatusWarning' : 'unavailableStatusInformation'}
              ns="profile-overview"
              values={{
                startDate: localeAwareFormat(new Date(startDate), 'EEE d MMMM'),
                endDate: localeAwareFormat(new Date(endDate), 'EEE d MMMM'),
              }}
            />
          </Text>
        </Column>

        <Button
          accessibilityLabel={t('profile.editUnavailabilityStatus', { ns: 'accessibility' })}
          onPress={onPress}
          testID="profile-unavailable-edit-button"
        >
          <IconPencil strokeWidth={ICON_STROKE_WIDTH} size={24} />
        </Button>
      </Row>

      <Button
        flex={1}
        disabled={deleteUnavailability.isPending}
        loading={deleteUnavailability.isPending}
        onPress={() => void deleteStatus()}
        testID="profile-unavailable-delete-button"
        variant="secondaryLight"
      >
        <Text>{t('deleteStatus')}</Text>
      </Button>
    </Column>
  );
};
